import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { CompanyService } from '../../../rent/service/data/company.service';
import { finalize, switchMap } from 'rxjs/operators';
import { FileService, IFileValidator } from '../../../core/service/file.service';
import { HttpClient } from '@angular/common/http';
import { ExistingRowStrategy, FeeChangeService } from '../../service/data/fee-change.service';
import { FeeChange } from '../../model/fee-change';
import { LocalDate } from '../../../core/model/local-date';
import { SelectionItem } from '../../../core/component/select/select.component';
import { TranslateService } from '@ngx-translate/core';

class FileValidator extends FileService {
  constructor(httpClient: HttpClient) {
    super(httpClient);
  }

  validateDocument(document: File): string {
    return ['xlsx'].includes(this.getExtension(document.name).toLowerCase()) ? null : 'FEE_CHANGE.IMPORT_XLSX_INCORRECT_FILETYPE';
  }
}

@Component({
    selector: 'fee-change-add-form',
    templateUrl: './fee-change-add-form.component.html',
    styleUrls: ['./fee-change-add-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: FeeChangeService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FeeChangeAddFormComponent extends FormComponentBase<FeeChange> {
  private readonly _defaultCaptionPrefixKey = 'FEE_CHANGE.CHANGE_TYPES.IMPORT';

  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _translateService: TranslateService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    private _service: FeeChangeService,
    messageService: MessageService,
    navigation: NavigationService,
    httpClient: HttpClient
  ) {
    super(activatedRoute, _service, messageService, navigation, commandDispatcher, handlerResolver);
    this.fileValidator = new FileValidator(httpClient);
  }

  protected createModel(): FeeChange {
    return new FeeChange();
  }

  protected override init(data: FeeChange) {
    super.init(data);

    this.AddSubscription(
      this._translateService.stream(this._defaultCaptionPrefixKey).subscribe(importCaption => {
        this._setDefaultCaption(importCaption);
      })
    );
  }

  fileValidator: IFileValidator;

  selectedFile: File;
  filesSelected(selection: FileList) {
    if (!selection || selection.length == 0) {
      this.selectedFile = null;
      this.form.controls['fileName'].setValue(null);
      this.form.controls['fileName'].markAllAsTouched();
      return;
    }
    this.selectedFile = selection.item(0);
    this.form.controls['fileName'].setValue(this.selectedFile.name);
    this.form.controls['fileName'].markAllAsTouched();
  }

  working: boolean = false;
  onSubmit() {
    this.form.markAllAsTouched();
    if (this.selectedFile == null || this.working || this.form.invalid) return;
    this.working = true;
    let target = this.form.controls['target'].value;
    this.AddSubscription(
      this._service
        .upload(
          this.selectedFile,
          this.form.controls['caption'].value,
          this.form.controls['conflictResolution'].value,
          target != 3,
          target != 2
        )
        .pipe(
          finalize(() => {
            this.working = false;
          })
        )
        .subscribe(id => {
          if (id) {
            this.form.markAsPristine();
            this.navigation.navigateToForm(id);
          }
        })
    );
  }

  get strategyDescription(): string {
    if (!this.form) return '';

    switch (this.form.controls['conflictResolution'].value) {
      case ExistingRowStrategy.AdjustEndDate:
        return 'FEE_CHANGE.CONFLICT_RESOLUTION.DESCRIPTION.ADJUST_END_DATE';
      case ExistingRowStrategy.Void:
        return 'FEE_CHANGE.CONFLICT_RESOLUTION.DESCRIPTION.VOID';
      case ExistingRowStrategy.Ignore:
        return 'FEE_CHANGE.CONFLICT_RESOLUTION.DESCRIPTION.IGNORE';
      case ExistingRowStrategy.SkipNew:
        return 'FEE_CHANGE.CONFLICT_RESOLUTION.DESCRIPTION.SKIP_NEW';
    }
    return '';
  }

  strategySelection: SelectionItem[] = [
    new SelectionItem('FEE_CHANGE.CONFLICT_RESOLUTION.ADJUST_END_DATE', ExistingRowStrategy.AdjustEndDate),
    new SelectionItem('FEE_CHANGE.CONFLICT_RESOLUTION.VOID', ExistingRowStrategy.Void),
    new SelectionItem('FEE_CHANGE.CONFLICT_RESOLUTION.IGNORE', ExistingRowStrategy.Ignore),
    new SelectionItem('FEE_CHANGE.CONFLICT_RESOLUTION.SKIP_NEW', ExistingRowStrategy.SkipNew),
  ];

  targetSelection: SelectionItem[] = [
    new SelectionItem('FEE_CHANGE.TARGETS.LEASE_AND_OBJECT', 1),
    new SelectionItem('FEE_CHANGE.TARGETS.LEASE_ONLY', 2),
    new SelectionItem('FEE_CHANGE.TARGETS.OBJECT_ONLY', 3),
  ];

  protected getFormGroup(): FormGroup {
    return this._fb.group({
      caption: this._fc.createMandatory('FEE_CHANGE.CAPTION', this._getDefaultCaption(this._defaultCaptionPrefixKey)),
      fileName: this._fc.createWithOptions('CONSUMPTION.IMPORT.IMPORT_IDENTIFIER', null, { isReadonly: true, required: true }),
      conflictResolution: this._fc.create('FEE_CHANGE.CONFLICT_RESOLUTION.STRATEGY', ExistingRowStrategy.AdjustEndDate),
      target: this._fc.create('FEE_CHANGE.TARGET', 1),
    });
  }

  private _getDefaultCaption(prefix: string): string {
    return `${prefix} ${LocalDate.today().toISOString()}`;
  }

  private _setDefaultCaption(importCaption: string) {
    const control = this.form?.controls['caption'];
    if (!control || control.dirty) return;

    const currentValue = control.value;
    const rawDefaultCaption = this._getDefaultCaption(this._defaultCaptionPrefixKey);

    if (!currentValue || currentValue === rawDefaultCaption) {
      control.setValue(this._getDefaultCaption(importCaption));
    }
  }
}
